import { Capacitor } from "@capacitor/core";

/**
 * iPad-vs-iPhone discriminator for the Capacitor mobile shell.
 *
 * `Capacitor.getPlatform()` returns `"ios"` for both iPad and iPhone, so we
 * fall back to the user-agent string. iPadOS 13+ reports as Macintosh with
 * touch (Apple's web-compat choice) — `navigator.maxTouchPoints > 1` is the
 * canonical way to discriminate that case.
 *
 * Caller is expected to gate on `Capacitor.isNativePlatform()` first.
 */
export function isIPad(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad/.test(ua)) return true;
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * True only inside the native Capacitor shell running on an iPad. This is the
 * single gate for iPad-only behavior (e.g. offline round caching), so
 * desktop/web and the iPhone build are left byte-identical.
 *
 * Test seam: a `e2eForceIPadCapacitor=true` localStorage flag forces this
 * true — in non-production builds, and in prod-optimized builds compiled with
 * `VITE_E2E_TEST_SEAMS=true` (the e2e prod-build mode; real deploys never set
 * it, so there the flag folds to false and the seam strips). Playwright isn't
 * a real Capacitor runtime, so this is the only way to exercise the
 * iPad-gated offline paths in the automated browser suite.
 */
export function isIPadCapacitor(): boolean {
  if (
    (import.meta.env?.PROD !== true ||
      import.meta.env?.VITE_E2E_TEST_SEAMS === "true") &&
    typeof localStorage !== "undefined" &&
    localStorage.getItem("e2eForceIPadCapacitor") === "true"
  ) {
    return true;
  }
  return Capacitor.isNativePlatform() && isIPad();
}
